import { useState, useMemo, useContext } from 'react';
import PrivateLayout from '@/components/layout/auth/PrivateLayout';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/hooks/useI18n';
import { useSharedLinks } from '@/lib/shared/hooks/useSharedLinks';
import { SharedLinksTable } from '@/lib/shared/SharedLinksTable';
import { SharedLinkModal } from '@/lib/shared/SharedLinkModal';
import {
  SharedLinksFilters,
  SharedLinkStatusFilter,
} from '@/lib/shared/components/SharedLinksFilters';
import { SharedLink, CreateSharedLinkData, UpdateSharedLinkData } from '@/lib/shared/models/shared-link.model';
import { Plus } from 'lucide-react';
import { AuthContext } from '@/lib/auth/models/auth.model';

const Shared = () => {
  const { t } = useI18n();
  const authContext = useContext(AuthContext);
  const userRole = authContext?.userRole;
  const userId = authContext?.userId;

  const {
    links,
    loading,
    createLink,
    updateLink,
    deleteLink,
  } = useSharedLinks();

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<SharedLinkStatusFilter>('all');
  // `null` = nessun designer scelto, coerente con come FacetCombobox rappresenta
  // l'assenza di filtro.
  const [designerFilter, setDesignerFilter] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingLink, setEditingLink] = useState<SharedLink | null>(null);

  // Check if user can see designer filter (admin or agency)
  const canFilterByDesigner = userRole === 'admin' || userRole === 'agency';

  // Opzioni del filtro designer, dai link gia' in memoria: nome come etichetta, email
  // come riga secondaria per distinguere gli omonimi.
  const designerOptions = useMemo(() => {
    if (!canFilterByDesigner) return undefined;
    const uniqueDesigners = new Map<string, string>();
    links.forEach((link) => {
      if (!uniqueDesigners.has(link.designer_email)) {
        uniqueDesigners.set(link.designer_email, link.designer_name);
      }
    });
    return Array.from(uniqueDesigners.entries())
      .map(([email, name]) => ({ value: email, label: name || email, hint: name ? email : null }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [links, canFilterByDesigner]);

  // Filter links based on search and filters
  const filteredLinks = useMemo(() => {
    return links.filter((link) => {
      // Search filter
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch =
        !searchQuery ||
        link.itinerary_name.toLowerCase().includes(searchLower) ||
        link.client_name?.toLowerCase().includes(searchLower) ||
        link.designer_name.toLowerCase().includes(searchLower);

      // Status filter
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && link.is_active) ||
        (statusFilter === 'expired' && !link.is_active);

      // Designer filter
      const matchesDesigner =
        designerFilter === null || link.designer_email === designerFilter;

      return matchesSearch && matchesStatus && (canFilterByDesigner ? matchesDesigner : true);
    });
  }, [links, searchQuery, statusFilter, designerFilter, canFilterByDesigner]);

  const handleCreateLink = () => {
    setEditingLink(null);
    setModalOpen(true);
  };

  const handleEditLink = (link: SharedLink) => {
    setEditingLink(link);
    setModalOpen(true);
  };

  const handleModalSubmit = async (
    data: CreateSharedLinkData | UpdateSharedLinkData,
    linkId?: string
  ): Promise<boolean> => {
    if (linkId) {
      // Edit mode
      return await updateLink(linkId, data as UpdateSharedLinkData);
    } else {
      // Create mode
      return await createLink(data as CreateSharedLinkData);
    }
  };

  const handleDeleteLink = async (id: string) => {
    await deleteLink(id);
  };

  return (
    <PrivateLayout
      pageTitle={t("shared.title")}
      pageDescription={t("shared.description")}
      showBackButton={false}
      contentClassName="max-w-7xl mx-auto h-full flex flex-col min-h-0 py-4"
      headerAction={
        <Button
          variant="default"
          size="lg"
          className="h-8 px-3"
          onClick={handleCreateLink}
        >
          <Plus className="h-4 w-4 mr-1" />
          {t("shared.createLink")}
        </Button>
      }
      headerActionCompact={
        <Button
          variant="default"
          size="sm"
          className="h-8 whitespace-nowrap rounded-full px-2.5 text-xs"
          onClick={handleCreateLink}
        >
          <Plus className="mr-1 h-3.5 w-3.5" />
          {t("shared.createLink")}
        </Button>
      }
    >
      <div className="flex flex-1 flex-col min-h-0 gap-4">
        <div className="shrink-0">
          <SharedLinksFilters
            status={statusFilter}
            onStatusChange={setStatusFilter}
            designerOptions={designerOptions}
            designerEmail={designerFilter}
            onDesignerChange={setDesignerFilter}
            search={searchQuery}
            onSearchChange={setSearchQuery}
          />
        </div>

        {/* Table */}
        <div className="flex-1 min-h-0">
          <SharedLinksTable
            links={filteredLinks}
            onEdit={handleEditLink}
            onDelete={handleDeleteLink}
            isLoading={loading}
          />
        </div>

        {/* Modal */}
        <SharedLinkModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onSubmit={handleModalSubmit}
          editingLink={editingLink}
        />
      </div>
    </PrivateLayout>
  );
};

export default Shared;
